import { useEffect, useState } from 'react'
import {
    X, LoaderCircle, CheckCircle, XCircle, FileText, Paperclip, ChevronRight, ChevronDown
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { updateCourseContentApproval, getLessons, getLessonsAttachs, getSubjectName } from '@/api/kuizy'
import { EXAM_STATUS, levelMapping } from '@/settings/master'

export default function ContentDetailModal({ visible, content, onClose, onUpdate }) {
    const [loading, setLoading] = useState(false)
    const [detailLoading, setDetailLoading] = useState(false)
    const [err, setErr] = useState(null)

    const [lessons, setLessons] = useState([])
    const [expandedLesson, setExpandedLesson] = useState(null)
    const [lessonAttachments, setLessonAttachments] = useState({}) // lessonId -> []
    const [loadingAttach, setLoadingAttach] = useState({}) // lessonId -> bool

    // If content passed is from the list, it might have approval_status
    // But we also need to fetch lessons/chapters to show "completeness"

    useEffect(() => {
        if (visible && content?.id) {
            fetchDetails()
        }
    }, [visible, content])

    const fetchDetails = async () => {
        setDetailLoading(true)
        try {
            // Fetch lessons for this content
            // identifying contentId: could be content.content_id (from course_contents view) or content.id if it's raw content
            const cid = content.content_id || content.id
            const res = await getLessonsAttachs({ contentId: cid })
            const lessonData = Array.isArray(res.data) ? res.data : (res.data?.data || [])
            setLessons(lessonData)
        } catch (e) {
            console.error(e)
        } finally {
            setDetailLoading(false)
        }
    }

    const toggleLesson = async (lessonId) => {
        if (expandedLesson === lessonId) {
            setExpandedLesson(null)
            return
        }
        setExpandedLesson(lessonId)

        // Fetch attachments if not loaded
        // if (!lessonAttachments[lessonId]) {
        //     setLoadingAttach(prev => ({ ...prev, [lessonId]: true }))
        //     try {
        //         const res = await getLessonsAttachs(lessonId)

        //         // structure might be res.data -> [ {id, check_type, ...} ]
        //         // check_type: 1=File, 2=Link, 3=Text
        //         setLessonAttachments(prev => ({ ...prev, [lessonId]: res.data.data || [] }))
        //     } catch (e) {
        //         console.error(e)
        //     } finally {
        //         setLoadingAttach(prev => ({ ...prev, [lessonId]: false }))
        //     }
        // }
    }

    const statusInfo = EXAM_STATUS[content?.approval_status] || { label: 'Unknown', style: 'bg-gray-100 text-gray-800' }

    const handleAction = async (status) => {
        // content.id in CourseContentModal context is the mapping ID (course_content.id)
        // Ensure we have the mapping id. If we opened this from a "general content list" we might not have it.
        // But since this is called from CourseContentModal, 'content' is the row from course_contents which has 'id' as the mapping id.

        if (!content?.id) return
        try {
            setLoading(true)
            await updateCourseContentApproval(content.id, status)
            onUpdate && onUpdate()
            onClose()
        } catch (e) {
            console.error(e)
            setErr(e.message || 'Error updating status')
        } finally {
            setLoading(false)
        }
    }

    if (!visible || !content) return null

    return (
        <AnimatePresence>
            <motion.div key="bg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/55 backdrop-blur-sm z-[1200] overflow-y-auto flex items-center justify-center p-4">
                <motion.div key="card" initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 20, opacity: 0 }} transition={{ duration: .2 }}
                    className="w-full max-w-2xl bg-white rounded-xl shadow-xl flex flex-col overflow-hidden max-h-[85vh]">

                    <header className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                        <h2 className="text-lg font-semibold text-gray-800">
                            รายละเอียดเนื้อหา
                        </h2>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                            <X size={20} />
                        </button>
                    </header>

                    <main className="flex-1 overflow-y-auto p-6">
                        {err && <div className="mb-4 text-red-600 bg-red-50 p-3 rounded">{err}</div>}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs text-gray-500 uppercase font-semibold">ชื่อเนื้อหา</label>
                                    <p className="text-base font-medium text-gray-900">{content.content_name || content.name || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 uppercase font-semibold">รหัสเนื้อหา</label>
                                    <p className="text-sm text-gray-700">{content.content_code || content.code || '-'}</p>
                                </div>
                                <div>
                                    <label className="text-xs text-gray-500 uppercase font-semibold">สถานะการอนุมัติ</label>
                                    <div className={`mt-1 inline-flex px-2 py-1 text-xs rounded border font-medium ${statusInfo.style}`}>
                                        {statusInfo.label}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-xs text-gray-500 uppercase font-semibold">รายละเอียด</label>
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{content.description || '-'}</p>
                                </div>
                            </div>
                        </div>

                        <div className="border-t pt-4">
                            <h3 className="font-semibold text-gray-800 mb-3">บทเรียน ({lessons.length})</h3>
                            {detailLoading ? (
                                <div className="flex justify-center py-4"><LoaderCircle className="animate-spin text-primary" /></div>
                            ) : lessons.length === 0 ? (
                                <p className="text-gray-500 text-sm text-center py-4">ไม่มีบทเรียน</p>
                            ) : (
                                <div className="space-y-2">
                                    {lessons.map((lesson, idx) => {
                                        const isExpanded = expandedLesson === lesson.lesson_id
                                        const attachments = lesson.attachments || []

                                        return (
                                            <div key={lesson.lesson_id} className="border rounded-lg overflow-hidden">
                                                <button
                                                    onClick={() => toggleLesson(lesson.lesson_id)}
                                                    className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-500 font-mono text-xs w-6">{idx + 1}.</span>
                                                        <span className="font-medium text-sm">{lesson.lesson_name}</span>
                                                        <span className="text-xs text-gray-500">
                                                            ({levelMapping[lesson.difficult_level || 1]})
                                                        </span>
                                                    </div>
                                                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                                </button>

                                                <AnimatePresence>
                                                    {isExpanded && (
                                                        <motion.div
                                                            initial={{ height: 0 }}
                                                            animate={{ height: 'auto' }}
                                                            exit={{ height: 0 }}
                                                            className="overflow-hidden"
                                                        >
                                                            <div className="p-3 bg-white border-t text-sm">
                                                                {attachments.length === 0 ? (
                                                                    <p className="text-gray-400 italic">ไม่มีสื่อการสอน</p>
                                                                ) : (
                                                                    <ul className="space-y-1">
                                                                        {attachments.map(att => {
                                                                            const isExternal = att.type === 'link' || att.type === 'youtube'
                                                                            return (
                                                                                <li key={att.id} className="flex items-center gap-2">
                                                                                    {att.check_type === 2
                                                                                        ? <FileText size={14} className="text-blue-500" />
                                                                                        : <Paperclip size={14} className="text-orange-500" />
                                                                                    }
                                                                                    {isExternal ? (
                                                                                        <a
                                                                                            href={att.url}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-blue-600 hover:underline hover:text-blue-700 transition"
                                                                                        >
                                                                                            {att.name}
                                                                                        </a>
                                                                                    ) : (
                                                                                        <span>{att.name}</span>
                                                                                    )}
                                                                                </li>
                                                                            )
                                                                        })}
                                                                    </ul>
                                                                )}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        )
                                    })}

                                </div>
                            )}
                        </div>

                    </main>

                    <footer className="p-4 border-t bg-white flex-shrink-0">
                        <div className="flex gap-3 justify-end">
                            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
                                ปิด
                            </button>
                            <button
                                onClick={() => handleAction(2)} // Reject
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                                {loading ? <LoaderCircle size={18} className="animate-spin" /> : <XCircle size={18} />}
                                ไม่อนุมัติ
                            </button>
                            <button
                                onClick={() => handleAction(1)} // Approve
                                disabled={loading}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50"
                            >
                                {loading ? <LoaderCircle size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                                อนุมัติ
                            </button>
                        </div>
                    </footer>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}
